/**
 * ScoreFloater — the Stage "slot-machine" floater.
 *
 * Two distinct modes (P3, Living Economy §8):
 *   • MENTION (🐦 tweet @mentions) → a single icon-only bird that drifts
 *     out the LEFT of the avatar, rise, and fade. NO number (each is a
 *     sub-cent fraction; the value is already banked as an aggregate). For
 *     Trump/Elon these stream out by the hundreds — the "alive" feeling.
 *   • EVENT (award, goal, election, death, …) → the classic Transport-Tycoon
 *     cash popup: an event emoji + `+₣ amount` drifting out the RIGHT, sized
 *     by magnitude (a ₣1,000,000 "settled outside earth" is a giant glowing
 *     pop; a ₣5 appearance is a small one).
 *
 * The mode is driven by the additive backend `visual` block when present
 * (`visual.kind`, `visual.icon`), and falls back to the event source/type so
 * legacy x_mention events (no visual block) still render as left birds.
 */
import { memo, useMemo } from 'react';
import { formatFrancAdaptive } from '../lib/utils';

/* Event-type → emoji. One symbol per category, no double icons. */
const EVENT_EMOJI = {
  // News
  award_win:        '🏆',
  election_won:     '🗳️',
  election_lost:    '💀',
  viral_moment:     '🔥',
  scandal:          '⚠️',
  achievement:      '⭐',
  // Video
  youtube_upload:   '📺',
  // Social
  x_mention:        '🐦',
  x_post:           '🐦',
  // Synthetic / admin
  synthetic:        '✨',
  manual_bonus:     '💰',
  manual_penalty:   '💸',
  diagnostic:       '🔧',
  // Sports
  goal:             '⚽',
  win:              '🥇',
  loss:             '🥈',
  // Default
  event:            '✨',
};

function emojiFor(serverEmoji, event_type, source) {
  if (serverEmoji) return serverEmoji;
  return (
    EVENT_EMOJI[event_type] ||
    EVENT_EMOJI[source] ||
    EVENT_EMOJI.event
  );
}

/** Magnitude tier for event sizing. Prefers the backend `visual.magnitude`
 *  (1..5), else derives from the absolute ₣ value. */
function magnitudeTier(absPoints, visualMag) {
  if (visualMag != null) {
    if (visualMag >= 5) return 'xl';
    if (visualMag >= 4) return 'lg';
    if (visualMag >= 3) return 'md';
    return 'sm';
  }
  if (absPoints >= 1000) return 'xl';
  if (absPoints >= 100) return 'lg';
  if (absPoints >= 5) return 'md';
  return 'sm';
}

// 2026-07-21 (owner request) — ONE delivered mention = ONE bird. Density now
// comes from the real (randomly-scheduled) arrival rate, not a per-floater
// flurry, so on-screen birds map 1:1 to delivered tweet samples.
const BIRD_COUNT = 1;

function ScoreFloaterInner({ points = 0, source = 'news', event_type = 'event', emoji = null, visual = null }) {
  const isMention = (visual?.kind === 'mention')
    || source === 'x_mention' || event_type === 'x_at_mention';
  const icon = visual?.icon || emoji || (isMention ? '🐦' : null);

  // Mention flurry — randomized leftward trajectories, stable per mount.
  const birds = useMemo(() => {
    if (!isMention) return [];
    return Array.from({ length: BIRD_COUNT }, (_, i) => ({
      key: i,
      x0: -4 + Math.random() * 8,
      xr: -(22 + Math.random() * 34),     // drift LEFT
      yr: -(40 + Math.random() * 36),     // rise
      rot: -12 + Math.random() * 24,
      delay: Math.round(i * (55 + Math.random() * 130)),
      dur: Math.round(1450 + Math.random() * 520),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Event drift — rightward, stable per mount (memoized so it doesn't
  // re-randomize and jitter mid-animation on a parent re-render).
  const eventVars = useMemo(() => {
    const xDrift = 20 + Math.floor(Math.random() * 16);   // 20 → 36px right
    const yRise = -56 - Math.floor(Math.random() * 18);   // -56 → -74px up
    const xStart = -4 + Math.floor(Math.random() * 8);
    const rotation = -1 + Math.random() * 2;
    return {
      '--x0': `${xStart}px`,
      '--xr': `${xDrift}px`,
      '--yr': `${yRise}px`,
      '--rot': `${rotation.toFixed(2)}deg`,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isMention) {
    return (
      <span className="score-floater-flurry" aria-hidden="true" data-testid="score-floater-mention">
        {birds.map((b) => (
          <span
            key={b.key}
            className="score-floater-bird"
            style={{
              '--x0': `${b.x0.toFixed(1)}px`,
              '--xr': `${b.xr.toFixed(1)}px`,
              '--yr': `${b.yr.toFixed(1)}px`,
              '--rot': `${b.rot.toFixed(1)}deg`,
              animationDelay: `${b.delay}ms`,
              animationDuration: `${b.dur}ms`,
            }}
          >
            {icon}
          </span>
        ))}
      </span>
    );
  }

  const isNegative = points < 0;
  const abs = formatFrancAdaptive(Math.abs(Number(points) || 0), { symbol: false });
  const symbol = emojiFor(emoji, event_type, source);
  const tier = magnitudeTier(Math.abs(Number(points) || 0), visual?.magnitude);

  return (
    <span
      className={`score-floater-ttd score-floater-ttd--event score-floater-ttd--${tier} ${isNegative ? 'score-floater-ttd--neg' : ''}`}
      style={eventVars}
      role="status"
      aria-live="polite"
      data-testid={`score-floater-${event_type}`}
    >
      <span className="score-floater-ttd-emoji" aria-hidden="true">{symbol}</span>
      <span className="score-floater-ttd-amt">
        {isNegative ? '−' : '+'}₣{abs}
      </span>
    </span>
  );
}

export default memo(ScoreFloaterInner);
